if (!customElements.get('premium-dropdown')) {
  customElements.define(
    'premium-dropdown',
    class PremiumDropdown extends HTMLElement {
      constructor() {
        super();
        this.handleDocumentClick = this.handleDocumentClick.bind(this);
        this.handleTriggerKeydown = this.handleTriggerKeydown.bind(this);
        this.handleListboxKeydown = this.handleListboxKeydown.bind(this);
      }

      connectedCallback() {
        this.select = this.querySelector('.premium-dropdown__native-select');
        this.trigger = this.querySelector('.premium-dropdown__trigger');
        this.valueEl = this.querySelector('.premium-dropdown__value');
        this.listbox = this.querySelector('.premium-dropdown__listbox');

        if (!this.select || !this.trigger || !this.listbox) return;

        this.unavailableStrings = [window.variantStrings?.unavailable, window.variantStrings?.soldOut].filter(
          Boolean
        );

        this.buildOptions();
        this.syncTriggerLabel();
        this.syncDisabledState();

        this.trigger.addEventListener('click', () => this.toggle());
        this.trigger.addEventListener('keydown', this.handleTriggerKeydown);
        this.listbox.addEventListener('keydown', this.handleListboxKeydown);
        this.select.addEventListener('change', () => {
          this.syncTriggerLabel();
          this.updateSelectedOption();
        });
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.handleDocumentClick);
      }

      isUnavailableText(text) {
        return this.unavailableStrings.some((str) => str && text.includes(str));
      }

      buildOptions() {
        this.listbox.innerHTML = '';
        this.optionEls = [];

        Array.from(this.select.options).forEach((opt, index) => {
          const li = document.createElement('li');
          li.setAttribute('role', 'option');
          li.setAttribute('id', `${this.id}-option-${index}`);
          li.dataset.value = opt.value;
          li.className = 'premium-dropdown__option';
          li.textContent = opt.textContent.trim();
          li.tabIndex = -1;

          if (this.isUnavailableText(li.textContent)) {
            li.classList.add('is-unavailable');
          }

          if (opt.selected) {
            li.classList.add('is-selected');
            li.setAttribute('aria-selected', 'true');
          } else {
            li.setAttribute('aria-selected', 'false');
          }

          li.addEventListener('click', () => this.selectOption(index));
          this.listbox.appendChild(li);
          this.optionEls.push(li);
        });
      }

      syncTriggerLabel() {
        const selected = this.select.options[this.select.selectedIndex];
        this.valueEl.textContent = selected ? selected.textContent.trim() : '';
      }

      syncDisabledState() {
        if (this.select.disabled) {
          this.setAttribute('data-disabled', '');
          this.trigger.setAttribute('aria-disabled', 'true');
          this.trigger.tabIndex = -1;
        } else {
          this.removeAttribute('data-disabled');
          this.trigger.removeAttribute('aria-disabled');
          this.trigger.tabIndex = 0;
        }
      }

      updateSelectedOption() {
        this.optionEls.forEach((li, index) => {
          const isSelected = index === this.select.selectedIndex;
          li.classList.toggle('is-selected', isSelected);
          li.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        });
      }

      get isOpen() {
        return !this.listbox.hasAttribute('hidden') === true && this.listbox.hidden === false;
      }

      toggle() {
        if (this.hasAttribute('data-disabled')) return;
        this.listbox.hidden ? this.open() : this.close();
      }

      open() {
        if (this.hasAttribute('data-disabled')) return;
        this.listbox.hidden = false;
        this.trigger.setAttribute('aria-expanded', 'true');
        this.setFocusedIndex(this.select.selectedIndex >= 0 ? this.select.selectedIndex : 0);
        document.addEventListener('click', this.handleDocumentClick);
      }

      close({ returnFocus = false } = {}) {
        this.listbox.hidden = true;
        this.trigger.setAttribute('aria-expanded', 'false');
        this.trigger.removeAttribute('aria-activedescendant');
        document.removeEventListener('click', this.handleDocumentClick);
        if (returnFocus) this.trigger.focus();
      }

      handleDocumentClick(event) {
        if (!this.contains(event.target)) this.close();
      }

      setFocusedIndex(index) {
        if (!this.optionEls.length) return;
        const clamped = Math.max(0, Math.min(index, this.optionEls.length - 1));
        this.optionEls.forEach((li) => li.classList.remove('is-focused'));
        const target = this.optionEls[clamped];
        target.classList.add('is-focused');
        this.trigger.setAttribute('aria-activedescendant', target.id);
        target.scrollIntoView({ block: 'nearest' });
        this.focusedIndex = clamped;
      }

      selectOption(index) {
        const opt = this.select.options[index];
        if (!opt) return;

        this.select.selectedIndex = index;
        this.select.dispatchEvent(new Event('change', { bubbles: true }));

        this.syncTriggerLabel();
        this.updateSelectedOption();
        this.close({ returnFocus: true });
      }

      handleTriggerKeydown(event) {
        switch (event.key) {
          case 'ArrowDown':
          case 'ArrowUp':
          case 'Enter':
          case ' ':
            event.preventDefault();
            if (!this.isOpen) {
              this.open();
            } else if (event.key === 'Enter' || event.key === ' ') {
              this.selectOption(this.focusedIndex ?? this.select.selectedIndex);
            }
            break;
          case 'Escape':
            if (this.isOpen) {
              event.preventDefault();
              this.close({ returnFocus: true });
            }
            break;
          default:
            break;
        }
      }

      handleListboxKeydown(event) {
        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault();
            this.setFocusedIndex((this.focusedIndex ?? 0) + 1);
            break;
          case 'ArrowUp':
            event.preventDefault();
            this.setFocusedIndex((this.focusedIndex ?? 0) - 1);
            break;
          case 'Home':
            event.preventDefault();
            this.setFocusedIndex(0);
            break;
          case 'End':
            event.preventDefault();
            this.setFocusedIndex(this.optionEls.length - 1);
            break;
          case 'Enter':
          case ' ':
            event.preventDefault();
            this.selectOption(this.focusedIndex ?? this.select.selectedIndex);
            break;
          case 'Escape':
            event.preventDefault();
            this.close({ returnFocus: true });
            break;
          case 'Tab':
            this.close();
            break;
          default:
            break;
        }
      }
    }
  );
}